import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

type State = {
  shouldShow: boolean;
  isChecking: boolean;
  checkedForUserId: string | null;
};

let state: State = {
  shouldShow: false,
  isChecking: true,
  checkedForUserId: null,
};

const listeners = new Set<() => void>();

function setState(next: Partial<State>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot() {
  return state;
}

let inFlightUserId: string | null = null;

async function runCheck(userId: string, email: string | undefined) {
  if (inFlightUserId === userId) return;
  inFlightUserId = userId;

  const isWhatsAppUser = email?.toLowerCase().endsWith("@wa.paystreem.app");
  if (!isWhatsAppUser) {
    setState({
      shouldShow: false,
      isChecking: false,
      checkedForUserId: userId,
    });
    inFlightUserId = null;
    return;
  }

  setState({ isChecking: true });
  const { data: profile } = await supabase
    .from("public_profiles")
    .select("verified_email")
    .eq("id", userId)
    .single();

  setState({
    shouldShow: !profile?.verified_email,
    isChecking: false,
    checkedForUserId: userId,
  });
  inFlightUserId = null;
}

export function markWhatsAppEmailVerified() {
  setState({ shouldShow: false, isChecking: false });
}

export function useWhatsAppEmailPrompt() {
  const { user } = useAuth();
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!user) {
      setState({
        shouldShow: false,
        isChecking: false,
        checkedForUserId: null,
      });
      return;
    }
    if (state.checkedForUserId !== user.id) {
      runCheck(user.id, user.email);
    }
  }, [user?.id, user?.email]);

  const close = () => {
    setState({ shouldShow: false });
  };

  return {
    shouldShow: snapshot.shouldShow,
    isChecking: snapshot.isChecking,
    close,
  };
}
